import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '../../components/idsk';
import { fieldError, type FieldOrder } from '../../components/common/FormErrors';
import type { InspectorFormValues } from '../../validation/schemas';
import type { Inspector } from '../../api/inspectors';
import { formatPhone } from '../../validation/rules';

export const EMPTY_INSPECTOR: InspectorFormValues = { titleBefore: '', firstName: '', lastName: '', titleAfter: '', personalNumber: '', phone: '', email: '' };

export const INSPECTOR_SERVER_FIELDS: Record<string, keyof InspectorFormValues> = {
  titleBefore: 'titleBefore',
  firstName: 'firstName',
  lastName: 'lastName',
  titleAfter: 'titleAfter',
  personalNumber: 'personalNumber',
  phone: 'phone',
  email: 'email',
};

export function inspectorFieldOrder(prefix: string): FieldOrder<InspectorFormValues>[] {
  return (['titleBefore', 'firstName', 'lastName', 'titleAfter', 'personalNumber', 'phone', 'email'] as const).map((name) => ({
    name,
    id: `${prefix}-${name}`,
  }));
}

export function inspectorToValues(i: Inspector): InspectorFormValues {
  return {
    titleBefore: i.titleBefore ?? '',
    firstName: i.firstName,
    lastName: i.lastName,
    titleAfter: i.titleAfter ?? '',
    personalNumber: i.personalNumber,
    phone: formatPhone(i.phone),
    email: i.email,
  };
}

/** All inspector fields with the WCAG 1.3.5 autocomplete tokens; used by the inspector form and the wizard. */
export function InspectorFields({
  prefix,
  register,
  errors,
}: {
  prefix: string;
  register: UseFormRegister<InspectorFormValues>;
  errors: FieldErrors<InspectorFormValues>;
}) {
  const { t } = useTranslation();
  const err = (name: keyof InspectorFormValues) => fieldError(t, errors[name]);
  return (
    <>
      <Input id={`${prefix}-titleBefore`} label={t('inspectors.fields.titleBefore')} hint={t('inspectors.hints.titleBefore')} autoComplete="honorific-prefix" width="10" error={err('titleBefore')} {...register('titleBefore')} />
      <Input id={`${prefix}-firstName`} label={t('inspectors.fields.firstName')} required autoComplete="given-name" width="one-half" spellCheck={false} error={err('firstName')} {...register('firstName')} />
      <Input id={`${prefix}-lastName`} label={t('inspectors.fields.lastName')} required autoComplete="family-name" width="one-half" spellCheck={false} error={err('lastName')} {...register('lastName')} />
      <Input id={`${prefix}-titleAfter`} label={t('inspectors.fields.titleAfter')} hint={t('inspectors.hints.titleAfter')} autoComplete="honorific-suffix" width="10" error={err('titleAfter')} {...register('titleAfter')} />
      <Input id={`${prefix}-personalNumber`} label={t('inspectors.fields.personalNumber')} required hint={t('inspectors.hints.personalNumber')} autoComplete="off" width="10" spellCheck={false} error={err('personalNumber')} {...register('personalNumber')} />
      <Input id={`${prefix}-phone`} label={t('inspectors.fields.phone')} required hint={t('inspectors.hints.phone')} type="tel" autoComplete="tel" width="20" error={err('phone')} {...register('phone')} />
      <Input id={`${prefix}-email`} label={t('inspectors.fields.email')} required type="email" autoComplete="email" spellCheck={false} width="two-thirds" error={err('email')} {...register('email')} />
    </>
  );
}
